import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { Icon, Button, Avatar, Badge, Field } from '@/components/ui';
import { useAccounts } from './hooks/useAccounts';
import { AccountsTab } from './lib/deriveRows';
import { AccountView } from './lib/mapAccount';
import { useIsMobile } from '@/components/main/shell/useIsMobile';
import { AccountsMobileList } from './AccountsMobileList';
import { AccountActionsModal } from './AccountActionsModal';
import { AccountsExportButton } from './AccountsExportButton';

const GRID =
  // Last column holds either the hover "View" affordance, or the "Inactive" badge plus a
  // Manage button — sized for the widest of those so none clips against the card's overflow.
  'tw-grid tw-items-center tw-gap-[16px] tw-px-[20px] [grid-template-columns:minmax(240px,1.4fr)_minmax(180px,1fr)_minmax(220px,1.1fr)_124px]';

const TabBtn = ({
  id,
  label,
  n,
  active,
  onSelect,
}: {
  id: AccountsTab;
  label: string;
  n: number;
  active: boolean;
  onSelect: (id: AccountsTab) => void;
}) => (
  <button
    onClick={() => onSelect(id)}
    className={clsx(
      'tw-inline-flex tw-items-center tw-gap-[8px] tw-h-[36px] tw-px-[18px] tw-rounded-full tw-font-poppins tw-text-[13.5px] tw-cursor-pointer tw-transition-colors',
      active
        ? 'tw-bg-ds-primary tw-text-white tw-font-semibold tw-border-0'
        : 'tw-bg-white tw-text-ds-ink-soft tw-font-medium tw-border tw-border-solid tw-border-ds-hairline'
    )}
  >
    {label}
    <span
      className={clsx(
        'tw-text-[11.5px] tw-font-semibold tw-rounded-full tw-px-[8px] tw-py-px',
        active
          ? 'tw-bg-white/[.22] tw-text-white'
          : 'tw-bg-ds-neutral-bg tw-text-ds-ink-mute'
      )}
    >
      {n}
    </span>
  </button>
);

const AccountsDesktopList = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  // Creation permissions from /api/me (see app.js → SET_ME_PERMISSIONS). Default-deny
  // shape mirrors the reducer's initial state so this never reads undefined.
  const permissions = useSelector(
    (s: any) =>
      s.project?.permissions || {
        canCreateCompany: false,
        canCreateProject: false,
      }
  );
  const [tab, setTab] = useState<AccountsTab>('companies');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [sortAsc, setSortAsc] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);
  // The account whose actions modal is open, or null. Owned here so the modal is a
  // sibling of the table rather than nested inside a clickable row.
  const [managing, setManaging] = useState<AccountView | null>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    },
    []
  );

  const {
    rows,
    isLoading,
    scope,
    companiesCount,
    projectsCount,
    activeCompaniesCount,
    activeProjectsCount,
  } = useAccounts({ tab, search, showInactive, sortAsc });

  const totalForTab = tab === 'companies' ? companiesCount : projectsCount;

  // Route: /projects/:projectId — root path renders ProjectDashboard (exact match).
  // Inactive accounts are a dead end: GET /api/project/detail/:id answers 400
  // "Inactive Project", the workspace never swaps in, and the shell is left showing
  // whichever account was open before. Block the navigation at the source instead.
  const openAccount = (row: AccountView) => {
    if (!row.active) return;
    history.push(`/projects/${row.id}`);
  };

  // Reuse the legacy add flow: AddCompany (rendered in app.js) reads project.AddType,
  // so we set it then open the Bootstrap modal. jQuery is a runtime global.
  const canCreateForTab =
    tab === 'companies'
      ? permissions.canCreateCompany
      : permissions.canCreateProject;
  const openCreate = () => {
    const addType = tab === 'companies' ? 'company' : 'project';
    dispatch({ type: 'SET_PROJECT_ADD_TYPE', addType });
    (window as any).$('#addCompanyModal').modal('show');
  };

  const copyEmail = (e: React.MouseEvent, email: string) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(email).catch(() => {});
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    setCopied(email);
    copyTimerRef.current = setTimeout(
      () => setCopied((c) => (c === email ? null : c)),
      1200
    );
  };

  return (
    <div className="tw-h-full tw-flex tw-flex-col tw-min-h-0 tw-bg-white">
      {/* header */}
      <div className="tw-px-[40px] tw-pt-[20px] tw-flex-none">
        <h1 className="app-h1">Accounts</h1>
        <p className="app-sm tw-mt-[4px]">
          {scope === 'all' ? 'There are ' : "You're assigned to "}
          <span className="tw-text-ds-ink-soft tw-font-semibold">
            {activeCompaniesCount} active companies
          </span>{' '}
          and {activeProjectsCount} active internal projects
          {scope === 'all' ? ' across all accounts.' : '.'}
        </p>
      </div>

      {/* controls */}
      <div className="tw-px-[40px] tw-pt-[16px] tw-pb-[14px] tw-flex-none tw-flex tw-items-center tw-gap-[12px] tw-flex-wrap">
        <div className="tw-flex tw-gap-[8px]">
          <TabBtn
            id="companies"
            label="Companies"
            n={companiesCount}
            active={tab === 'companies'}
            onSelect={setTab}
          />
          <TabBtn
            id="projects"
            label="Internal Projects"
            n={projectsCount}
            active={tab === 'projects'}
            onSelect={setTab}
          />
        </div>
        <div className="tw-ml-auto tw-flex tw-items-center tw-gap-[14px]">
          <label className="tw-inline-flex tw-items-center tw-gap-[8px] tw-cursor-pointer tw-select-none">
            <input
              type="checkbox"
              className="tw-m-0"
              style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }}
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span
              className={clsx(
                'tw-w-[36px] tw-h-[20px] tw-rounded-full tw-relative tw-transition-colors tw-flex-none',
                showInactive ? 'tw-bg-ds-primary' : 'tw-bg-ds-field-disabled'
              )}
            >
              <span
                className={clsx(
                  'tw-absolute tw-top-[2px] tw-w-[16px] tw-h-[16px] tw-rounded-full tw-bg-white tw-shadow-ds-e1 tw-transition-all',
                  showInactive ? 'tw-left-[18px]' : 'tw-left-[2px]'
                )}
              />
            </span>
            <span className="tw-font-poppins tw-text-[13px] tw-text-ds-ink-soft">
              Show inactive
            </span>
          </label>
          <div className="tw-w-[240px] tw-max-w-[28vw]">
            <Field
              icon="search"
              placeholder={
                tab === 'companies'
                  ? 'Search companies…'
                  : 'Search internal projects…'
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch('')}
            />
          </div>
          <AccountsExportButton rows={rows} tab={tab} />
          {/* New company/project is gated by BE permissions (/api/me). Staff and guests
              can't create companies; the BE also enforces this on POST /api/project. */}
          {canCreateForTab && (
            <Button
              variant="primary"
              icon="plus"
              size="sm"
              onClick={openCreate}
            >
              {tab === 'companies' ? 'New company' : 'New internal project'}
            </Button>
          )}
        </div>
      </div>

      {/* table */}
      <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-px-[40px] tw-pb-[32px]">
        <div className="tw-border tw-border-ds-hairline tw-rounded-ds-l tw-overflow-hidden">
          {/* header row */}
          <div
            className={clsx(
              GRID,
              'tw-h-[44px] tw-bg-ds-table-header tw-border-b tw-border-ds-hairline'
            )}
          >
            <button
              onClick={() => setSortAsc((x) => !x)}
              className="app-label tw-inline-flex tw-items-center tw-gap-[6px] tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-0"
            >
              {tab === 'companies' ? 'Company name' : 'Project name'}
              <Icon
                name={sortAsc ? 'arrow-up' : 'arrow-down'}
                size={14}
                color="var(--twilight-sky)"
              />
            </button>
            <span className="app-label">Manager</span>
            <span className="app-label">Inbox email</span>
            <span />
          </div>

          {/* loading */}
          {isLoading && (
            <div className="tw-px-[20px] tw-py-[56px] tw-text-center tw-font-poppins tw-text-[14px] tw-text-ds-ink-mute">
              Loading accounts…
            </div>
          )}

          {/* data rows */}
          {!isLoading &&
            rows.map((c, i) => (
              <div
                key={c.id}
                role={c.active ? 'button' : undefined}
                aria-disabled={c.active ? undefined : true}
                tabIndex={c.active ? 0 : undefined}
                onClick={() => openAccount(c)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openAccount(c);
                  }
                }}
                className={clsx(
                  GRID,
                  'tw-min-h-[48px] tw-bg-white tw-transition-colors tw-group',
                  c.active
                    ? 'tw-cursor-pointer hover:tw-bg-ds-sky-mist'
                    : 'tw-cursor-default',
                  i === rows.length - 1
                    ? ''
                    : 'tw-border-b tw-border-ds-hairline'
                )}
              >
                <div className="tw-flex tw-items-center tw-gap-[10px] tw-min-w-0">
                  <span
                    title={c.active ? 'Active' : 'Inactive'}
                    className={clsx(
                      'tw-w-[8px] tw-h-[8px] tw-rounded-full tw-flex-none',
                      c.active ? 'tw-bg-ds-success' : 'tw-bg-ds-ink-mute'
                    )}
                  />
                  <span
                    className={clsx(
                      'tw-font-poppins tw-text-[14px] tw-font-medium tw-truncate',
                      c.active ? 'tw-text-ds-ink' : 'tw-text-ds-ink-mute'
                    )}
                  >
                    {c.name}
                  </span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-[10px] tw-min-w-0">
                  <Avatar
                    name={c.managerName}
                    src={c.managerAvatarUrl}
                    size={26}
                  />
                  <span className="tw-font-poppins tw-text-[13.5px] tw-text-ds-storm tw-truncate">
                    {c.managerName}
                  </span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-[8px] tw-min-w-0">
                  <span className="tw-font-poppins tw-text-[13px] tw-text-ds-ink-soft tw-truncate">
                    {c.email}
                  </span>
                  <button
                    onClick={(e) => copyEmail(e, c.email)}
                    title="Copy email"
                    className="tw-border-0 tw-bg-transparent tw-cursor-pointer tw-flex tw-p-[2px] tw-flex-none tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity"
                  >
                    <Icon
                      name={copied === c.email ? 'check' : 'copy'}
                      size={15}
                      color={
                        copied === c.email
                          ? 'var(--icon-success)'
                          : 'var(--ink-mute)'
                      }
                    />
                  </button>
                </div>
                <div className="tw-flex tw-justify-end tw-items-center tw-gap-[8px]">
                  {c.active ? (
                    <span className="tw-font-poppins tw-text-[13px] tw-font-semibold tw-text-ds-primary tw-inline-flex tw-items-center tw-gap-[4px] tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
                      View{' '}
                      <Icon
                        name="chevron-right"
                        size={15}
                        color="var(--twilight-sky)"
                      />
                    </span>
                  ) : (
                    <>
                      <Badge>Inactive</Badge>
                      {c.canManage && (
                        <button
                          type="button"
                          aria-label={`Manage ${c.name}`}
                          title="Manage account"
                          onClick={(e) => {
                            e.stopPropagation();
                            setManaging(c);
                          }}
                          className="tw-border-0 tw-bg-transparent tw-cursor-pointer tw-flex tw-p-[2px] tw-flex-none"
                        >
                          <Icon
                            name="pencil"
                            size={15}
                            color="var(--ink-mute)"
                          />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}

          {/* empty */}
          {!isLoading && rows.length === 0 && (
            <div className="tw-px-[20px] tw-py-[56px] tw-flex tw-flex-col tw-items-center tw-gap-[12px]">
              <Icon name="search-x" size={28} color="var(--ink-mute)" />
              <span className="tw-font-poppins tw-text-[14px] tw-text-ds-ink-mute">
                No accounts match your filters.
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearch('');
                  setShowInactive(true);
                }}
              >
                Clear filters
              </Button>
            </div>
          )}
        </div>

        {/* footer count */}
        {!isLoading && rows.length > 0 && (
          <div className="tw-mt-[14px]">
            <span className="app-sm">
              Showing {rows.length} of {totalForTab}{' '}
              {tab === 'companies' ? 'companies' : 'projects'}
            </span>
          </div>
        )}
      </div>

      <AccountActionsModal
        account={managing}
        onClose={() => setManaging(null)}
      />
    </div>
  );
};

// Viewport switch. Each branch is its own component so the heavy desktop hooks
// (useAccounts + local filter state) never run on phones, and vice versa —
// React only mounts one branch at a time.
export const AccountsPage = () => {
  const isMobile = useIsMobile();
  return isMobile ? <AccountsMobileList /> : <AccountsDesktopList />;
};

export default AccountsPage;
